import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

const inputCls = "h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none ring-primary transition-shadow focus:ring-2";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md px-5 py-20 sm:py-28" data-testid="forgot-password-page">
      <p className="eyebrow">Forgot password</p>
      <h1 className="mt-3 text-4xl font-medium tracking-tight">Let's get you back to your shelf.</h1>
      {sent ? (
        <div className="mt-8 rounded-3xl bg-white p-8 ring-1 ring-border/70" data-testid="forgot-sent-message">
          <p className="text-sm leading-relaxed">If an account exists for <strong>{email}</strong>, a reset link is on its way. It works for one hour.</p>
          <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-primary" data-testid="forgot-back-to-login">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 rounded-3xl bg-white p-8 ring-1 ring-border/70">
          <p className="mb-5 text-sm text-muted-foreground">Enter the email you signed up with and we'll send a link to choose a new password.</p>
          <input required type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email-input" />
          <button type="submit" disabled={busy} className="mt-5 h-12 w-full rounded-full bg-foreground text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="forgot-submit-button">{busy ? "Sending…" : "Send reset link"}</button>
          <p className="mt-5 text-center text-sm text-muted-foreground"><Link to="/login" className="font-semibold text-primary" data-testid="forgot-login-link">Back to sign in</Link></p>
        </form>
      )}
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.a !== pw.b) return setError("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw.a });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md px-5 py-20 sm:py-28" data-testid="reset-password-page">
      <p className="eyebrow">New password</p>
      <h1 className="mt-3 text-4xl font-medium tracking-tight">Choose something memorable.</h1>
      <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-white p-8 ring-1 ring-border/70">
        {!token && <p className="rounded-xl bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="reset-missing-token">This link is missing its token. <Link to="/forgot-password" className="underline">Request a new one</Link>.</p>}
        <input required type="password" minLength={6} placeholder="New password (min 6 characters)" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className={inputCls} data-testid="reset-password-input" />
        <input required type="password" minLength={6} placeholder="Repeat new password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className={inputCls} data-testid="reset-password-confirm-input" />
        {error && <p className="rounded-xl bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="reset-error">{error}</p>}
        <button type="submit" disabled={busy || !token} className="h-12 w-full rounded-full bg-foreground text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="reset-submit-button">{busy ? "Saving…" : "Set new password"}</button>
      </form>
    </div>
  );
}
